// Format currency
export function formatCurrency(amount: number, currency: string = 'RM'): string {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
  }).format(amount)
}

// Format date
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return new Intl.DateTimeFormat('en-MY', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

// Format short date (e.g., 27 Nov 2025)
export function formatShortDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dateObj)
}

// Format date and time
export function formatDateTime(date: string | Date): string {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Format time
export function formatTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return new Intl.DateTimeFormat('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

// Format phone number (Malaysian or Singaporean format)
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '')

  // Check if it's a Malaysian number
  if (cleaned.startsWith('60')) {
    // Format as +60 XX-XXXX XXXX or +60 X-XXX XXXXX
    if (cleaned.length === 11) { // Mobile
      return `+60 ${cleaned.slice(2, 4)}-${cleaned.slice(4, 8)} ${cleaned.slice(8)}`
    } else if (cleaned.length === 10) { // Landline
      return `+60 ${cleaned.slice(2, 3)}-${cleaned.slice(3, 7)} ${cleaned.slice(7)}`
    }
  }

  // Check if it's a Singaporean number (+65 XXXX XXXX)
  if (cleaned.startsWith('65') && cleaned.length === 10) {
    return `+65 ${cleaned.slice(2, 6)} ${cleaned.slice(6)}`
  }

  // Return original if can't format
  return phone
}

// Format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Truncate text
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

// Generate case number
export function generateCaseNumber(): string {
  const date = new Date()
  const year = date.getFullYear().toString().slice(-2)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')

  return `AJ${year}${month}${day}${random}`
}

// Calculate age from date of birth
export function calculateAge(dateOfBirth: string | Date): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

// Get status color
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    INITIATED: 'bg-blue-100 text-blue-800',
    DOCUMENTS_PENDING: 'bg-yellow-100 text-yellow-800',
    TRANSPORT_SCHEDULED: 'bg-purple-100 text-purple-800',
    GHUSL_SCHEDULED: 'bg-indigo-100 text-indigo-800',
    KAFAN_SCHEDULED: 'bg-pink-100 text-pink-800',
    JANAZAH_SCHEDULED: 'bg-orange-100 text-orange-800',
    BURIAL_SCHEDULED: 'bg-red-100 text-red-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    CONFIRMED: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-indigo-100 text-indigo-800',
    PAID: 'bg-green-100 text-green-800',
    UNPAID: 'bg-red-100 text-red-800',
  }

  return statusColors[status] || 'bg-gray-100 text-gray-800'
}

// Format duration
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (hours === 0) {
    return `${mins} minute${mins !== 1 ? 's' : ''}`
  } else if (mins === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  } else {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`
  }
}

// Get consistent payment status messages for burial plot bookings
export function getBurialPlotPaymentStatus(booking: any): {
  isPaymentRequired: boolean;
  isPaymentSubmitted: boolean;
  isPaymentVerified: boolean;
  statusMessage: string;
  detailMessage: string;
  actionRequired: boolean;
} {
  const bookingStatus = booking.status;
  const payment = booking.payment || {};
  const paymentStatus = payment.status;
  const verificationStatus = payment.verificationStatus;

  // Check for payment submission indicators
  const hasTransferReference = booking.transferReference || payment.transactionReference;
  const hasReceipt = payment.transferProofUrl || payment.receiptImageUrl;

  // Payment verified/completed - only when payment is actually verified
  if (
    paymentStatus === 'VERIFIED' ||
    paymentStatus === 'PAID' ||
    verificationStatus === 'VERIFIED'
  ) {
    // Check the current booking status to provide appropriate messaging
    let statusMessage = 'Payment Verified';
    let detailMessage = 'Payment verified and awaiting provider confirmation';

    if (bookingStatus === 'COMPLETED') {
      statusMessage = 'Service Completed';
      detailMessage = 'Payment verified and service completed';
    } else if (bookingStatus === 'IN_PROGRESS') {
      statusMessage = 'Service In Progress';
      detailMessage = 'Payment verified and service in progress';
    } else if (bookingStatus === 'CONFIRMED') {
      statusMessage = 'Booking Confirmed';
      detailMessage = 'Payment verified and booking confirmed by provider';
    }

    return {
      isPaymentRequired: false,
      isPaymentSubmitted: true,
      isPaymentVerified: true,
      statusMessage,
      detailMessage,
      actionRequired: false,
    };
  }

  // Provider confirmed but payment not verified yet
  if (
    (bookingStatus === 'CONFIRMED' || bookingStatus === 'IN_PROGRESS') &&
    paymentStatus !== 'VERIFIED' &&
    paymentStatus !== 'PAID' &&
    verificationStatus !== 'VERIFIED'
  ) {
    const hasPaymentSubmission = hasReceipt || (payment && payment.id);
    return {
      isPaymentRequired: false,
      isPaymentSubmitted: hasPaymentSubmission,
      isPaymentVerified: false,
      statusMessage: hasPaymentSubmission ? 'Payment Submitted' : 'Awaiting Payment',
      detailMessage: hasPaymentSubmission
        ? `Payment is pending verification. Transfer reference: ${hasTransferReference || booking.transferReference || 'N/A'}`
        : `Awaiting payment submission. Transfer reference: ${hasTransferReference || booking.transferReference || 'N/A'}`,
      actionRequired: !hasPaymentSubmission,
    };
  }

  // Payment submitted but pending verification
  // Only consider payment submitted if there's actual proof of payment (receipt/proof) OR explicit payment record
  if (
    hasReceipt || // Receipt uploaded
    (payment && payment.id && paymentStatus === 'PENDING') || // Explicit payment record with PENDING status
    (paymentStatus === 'PENDING' && verificationStatus === 'PENDING') // Payment record exists and is pending
  ) {
    const transferRef = hasTransferReference || payment.transactionReference || booking.transferReference || 'N/A';
    return {
      isPaymentRequired: false,
      isPaymentSubmitted: true,
      isPaymentVerified: false,
      statusMessage: 'Payment Submitted',
      detailMessage: `Payment is pending verification. Transfer reference: ${transferRef}`,
      actionRequired: false,
    };
  }

  // Payment required (booking created but no payment yet)
  if (
    bookingStatus === 'PENDING' &&
    !hasReceipt &&
    (!paymentStatus || paymentStatus === 'PENDING') &&
    (!verificationStatus || verificationStatus === 'PENDING')
  ) {
    const transferRef = hasTransferReference || booking.transferReference || 'N/A';
    return {
      isPaymentRequired: true,
      isPaymentSubmitted: false,
      isPaymentVerified: false,
      statusMessage: 'Payment Required',
      detailMessage: `Payment required to confirm booking. Transfer reference: ${transferRef}`,
      actionRequired: true,
    };
  }

  // Default fallback
  return {
    isPaymentRequired: false,
    isPaymentSubmitted: false,
    isPaymentVerified: false,
    statusMessage: 'Payment Pending',
    detailMessage: 'Payment status pending',
    actionRequired: false,
  };
}

// Format status text with professional mappings
export function formatStatus(status: string): string {
  const statusMappings: Record<string, string> = {
    // Booking statuses
    'PENDING': 'Pending',
    'CONFIRMED': 'Confirmed',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'CANCELLED': 'Cancelled',

    // Workflow steps
    'BOOKING_CREATED': 'Booking Created',
    'PAYMENT_SUBMITTED': 'Payment Submitted',
    'AWAITING_VERIFICATION': 'Payment Verification',
    'PAYMENT_VERIFIED': 'Payment Confirmed',
    'PROVIDER_NOTIFIED': 'Provider Assigned',
    'SERVICE_IN_PROGRESS': 'Service in Progress',
    'SERVICE_COMPLETED': 'Service Completed',
    'TRIP_IN_PROGRESS': 'Trip in Progress',
    'PROVIDER_CONFIRMED': 'Provider Confirmed',
    'AWAITING_ARRIVAL': 'Awaiting Arrival',
    'ALL_DONE': 'Completed', // Fallback for existing data

    // Case statuses
    'INITIATED': 'Initiated',
    'DOCUMENTS_PENDING': 'Documents Pending',
    'TRANSPORT_SCHEDULED': 'Transport Scheduled',
    'GHUSL_SCHEDULED': 'Ghusl Scheduled',
    'KAFAN_SCHEDULED': 'Kafan Scheduled',
    'JANAZAH_SCHEDULED': 'Janazah Scheduled',
    'BURIAL_SCHEDULED': 'Burial Scheduled',

    // Payment statuses
    'PAID': 'Paid',
    'UNPAID': 'Unpaid',
    'PARTIAL': 'Partially Paid',
    'VERIFIED': 'Verified',

    // Legacy fallback
    'AWAITING_PAYMENT': 'Awaiting Payment',
  };

  // Return mapped status or format the original
  return statusMappings[status] ||
         status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
}

